import { useState, useEffect } from "react";
import { N8N_BASE, FF, GF, SPill, GlassCard, INP, SEL, fmt, TabBar, SkeletonRows, StatCard } from "../shared";

const GET_INVOICES_URL  = `${N8N_BASE}/qc-invoices-get`;
const GET_CAMPAIGNS_URL = `${N8N_BASE}/qc-campaigns-get`;
const ASSIGN_URL        = `${N8N_BASE}/qc-invoice-assign`;

const NET_DAYS_OPTIONS = [
  { value: 30, label: "Net 30" },
  { value: 45, label: "Net 45" },
  { value: 60, label: "Net 60" },
];

// Parse DD/MM/YYYY or YYYY-MM-DD → local Date (no UTC offset shift)
function parseLocalDate(s) {
  if (!s) return null;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split("/");
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return new Date(s);
}

// Handle both DD/MM/YYYY (legacy) and YYYY-MM-DD (new)
function fmtDate(d) {
  if (!d) return null;
  const parsed = parseLocalDate(d);
  if (!parsed || isNaN(parsed)) return d;
  return parsed.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

function addDays(dateStr, days) {
  if (!dateStr) return null;
  const d = parseLocalDate(dateStr);
  if (!d || isNaN(d)) return null;
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

// Parse DD/MM/YYYY or YYYY-MM-DD → ms timestamp (for sorting)
function toTs(d) {
  if (!d) return 0;
  const t = parseLocalDate(d);
  return t && !isNaN(t) ? t.getTime() : 0;
}

function groupByCampaign(invoices) {
  const map = {};
  for (const inv of invoices) {
    const key = inv.campaign_id || inv.campaign_name || "unknown";
    if (!map[key]) map[key] = { campaign_name: inv.campaign_name, brand: inv.brand, campaign_id: inv.campaign_id, invoices: [] };
    map[key].invoices.push(inv);
  }
  return Object.values(map);
}

const STATUS_RANK = { unpaid: 0, pending_payment: 0, draft: 1, paid: 2 };
function sortGroups(groups, sort) {
  return [...groups].sort((a, b) => {
    if (sort === "status") {
      const rank = (g) => Math.min(...g.invoices.map((i) => STATUS_RANK[i.status] ?? 3));
      return rank(a) - rank(b);
    }
    if (sort === "due_asc" || sort === "due_desc") {
      // Use earliest due_date in the group
      const earliest = (g) => Math.min(...g.invoices.map((i) => toTs(i.due_date) || Infinity));
      const diff = earliest(a) - earliest(b);
      return sort === "due_asc" ? diff : -diff;
    }
    if (sort === "amount_desc" || sort === "amount_asc") {
      const total = (g) => g.invoices.reduce((s, i) => s + (Number(i.invoice_total) || 0), 0);
      const diff = total(b) - total(a);
      return sort === "amount_desc" ? diff : -diff;
    }
    return 0;
  });
}



// ── Main page ─────────────────────────────────────────────────────────────────
export default function InvoicesPage() {
  const [tab, setTab]           = useState("assign");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const [expandedId, setExpandedId] = useState(null);
  const [forms, setForms]           = useState({});
  const [saving, setSaving]         = useState({});
  const [messages, setMessages]     = useState({});

  // All Invoices tab controls
  const [allSearch, setAllSearch]   = useState("");
  const [allSort, setAllSort]       = useState("status");
  const [showNoInvoices, setShowNoInvoices] = useState(false);

  // Campaigns (for "no invoices" filter)
  const [campaigns, setCampaigns]       = useState([]);
  const [campsLoading, setCampsLoading] = useState(false);
  const [campsFetched, setCampsFetched] = useState(false);

  const fetchInvoices = async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(GET_INVOICES_URL);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      // Response shape: [{ today: "...", invoices: [...] }]
      const list = Array.isArray(d) ? (d[0]?.invoices || d) : (d.invoices || []);
      setInvoices(Array.isArray(list) ? list : []);
    } catch {
      setError("Could not load invoices. Check your connection.");
    } finally { setLoading(false); }
  };

  const fetchCampaigns = async () => {
    setCampsLoading(true);
    try {
      const r = await fetch(GET_CAMPAIGNS_URL);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      const list = Array.isArray(d) ? d : (d.campaigns || []);
      setCampaigns(Array.isArray(list) ? list : []);
      setCampsFetched(true);
    } catch {
      // silently fail — user can retry via toggle
    } finally { setCampsLoading(false); }
  };

  useEffect(() => { fetchInvoices(); }, []);

  // Lazy-fetch campaigns when toggle is first activated
  useEffect(() => {
    if (showNoInvoices && !campsFetched && !campsLoading) fetchCampaigns();
  }, [showNoInvoices]); // eslint-disable-line

  const toggleExpand = (inv) => {
    const id = inv.row_number;
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!forms[id]) setForms((f) => ({ ...f, [id]: { invoice_number: "", billed_date: "", net_days: 45 } }));
    setMessages((m) => { const n = { ...m }; delete n[id]; return n; });
  };

  const updateForm = (id, field, value) =>
    setForms((f) => ({ ...f, [id]: { ...f[id], [field]: value } }));

  const handleAssign = async (inv) => {
    const id = inv.row_number;
    const f  = forms[id] || {};
    if (!f.invoice_number?.trim()) {
      setMessages((m) => ({ ...m, [id]: { type: "error", msg: "Invoice number is required." } }));
      return;
    }
    if (!f.billed_date) {
      setMessages((m) => ({ ...m, [id]: { type: "error", msg: "Billed date is required." } }));
      return;
    }
    setSaving((s) => ({ ...s, [id]: true }));
    setMessages((m) => { const n = { ...m }; delete n[id]; return n; });
    try {
      const payload = {
        invoice_number: f.invoice_number.trim(),
        campaign_id:    inv.campaign_id,
        billed_date:    f.billed_date,
        net_days:       Number(f.net_days) || 45,
        ...(inv.campaign_name ? { campaign_name: inv.campaign_name } : {}),
      };
      const r = await fetch(ASSIGN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMessages((m) => ({
        ...m,
        [id]: { type: "success", msg: `${f.invoice_number} assigned — PDF uploaded to Dropbox.` },
      }));
      setExpandedId(null);
      fetchInvoices();
    } catch {
      setMessages((m) => ({ ...m, [id]: { type: "error", msg: "Failed to assign. Check connection." } }));
    } finally { setSaving((s) => ({ ...s, [id]: false })); }
  };

  // ── Computed ────────────────────────────────────────────────────────────────
  // Draft = status "draft" AND no invoice_number
  const draftInvoices  = invoices.filter((i) => i.status === "draft" && !i.invoice_number);
  const unpaidInvoices = invoices.filter((i) => (i.status === "unpaid" || i.status === "pending_payment") && i.invoice_number);
  const paidInvoices   = invoices.filter((i) => i.status === "paid");
  const pendingValue   = unpaidInvoices.reduce((s, i) => s + (Number(i.invoice_total) || 0), 0);

  const allGroups = groupByCampaign(invoices);
  const filteredGroups = sortGroups(
    allSearch
      ? allGroups.filter((g) =>
          g.campaign_name?.toLowerCase().includes(allSearch.toLowerCase()) ||
          g.brand?.toLowerCase().includes(allSearch.toLowerCase())
        )
      : allGroups,
    allSort
  );
  const fmtCAD  = (n) => Number(n).toLocaleString("en-CA", { maximumFractionDigits: 0 });

  // Campaigns with no invoices linked
  const invoicedCampaignIds = new Set(invoices.map((i) => i.campaign_id).filter(Boolean));
  const campaignsWithNoInvoices = campaigns.filter(
    (c) => c.campaign_id && !invoicedCampaignIds.has(c.campaign_id) && c.status !== "completed"
  );

  const subPanel = { background: "rgba(255,255,255,0.45)", border: "0.5px solid rgba(255,255,255,0.78)" };

  // ── Inline assign form ────────────────────────────────────────────────────
  const AssignForm = ({ inv }) => {
    const id        = inv.row_number;
    const f         = forms[id] || { invoice_number: "", billed_date: "", net_days: 45 };
    const msg       = messages[id];
    const isSaving  = saving[id];
    const net       = Number(inv.net_after_tax) || 0;
    const total     = Number(inv.invoice_total) || 0;
    const hst       = total - net;

    return (
      <div style={{ margin: "0 0 14px", padding: "16px 18px", ...subPanel, borderRadius: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(60,60,67,0.38)", letterSpacing: "0.7px", textTransform: "uppercase", marginBottom: 14, fontFamily: FF }}>
          Assign Invoice
        </div>

        <div className="inv-amount-flex" style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[
            { label: "Net",   value: `$${fmt(net)}`,   bold: false },
            { label: "HST",   value: `$${fmt(hst)}`,   bold: false },
            { label: "Total", value: `$${fmt(total)}`,  bold: true  },
          ].map((item) => (
            <div key={item.label} style={{
              flex: 1, minWidth: 80, padding: "9px 11px",
              background: item.bold ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.42)",
              border: "0.5px solid rgba(255,255,255,0.82)",
              borderRadius: 10, textAlign: "center",
            }}>
              <div style={{ fontSize: 9, color: "rgba(60,60,67,0.42)", fontFamily: FF, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>{item.label}</div>
              <div style={{ fontSize: 14, fontWeight: item.bold ? 700 : 600, color: "#1c1c1e", fontFamily: FF, letterSpacing: "-0.3px" }}>{item.value}</div>
            </div>
          ))}
        </div>

        <div className="form-grid-3" style={{ marginBottom: 0 }}>
          <GF label="Invoice Number" required>
            <input className="g-input" style={{ ...INP, fontSize: 13 }}
              value={f.invoice_number}
              onChange={(e) => updateForm(id, "invoice_number", e.target.value)}
              placeholder="e.g. LUL-060826" />
          </GF>
          <GF label="Billed Date" required>
            <input className="g-input" style={{ ...INP, fontSize: 13 }}
              type="date" value={f.billed_date}
              onChange={(e) => updateForm(id, "billed_date", e.target.value)} />
          </GF>
          <GF label="Net Days">
            <select className="g-select" style={{ ...SEL, fontSize: 13 }}
              value={f.net_days}
              onChange={(e) => updateForm(id, "net_days", e.target.value)}>
              {NET_DAYS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </GF>
        </div>

        {f.billed_date && (
          <div style={{ marginTop: 10, marginBottom: 12, fontSize: 12, color: "rgba(60,60,67,0.48)", fontFamily: FF, display: "flex", alignItems: "center", gap: 5 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Due {addDays(f.billed_date, Number(f.net_days) || 45)}
          </div>
        )}

        {msg && (
          <div style={{
            marginBottom: 10, padding: "7px 11px", borderRadius: 8, fontSize: 12, fontWeight: 500, fontFamily: FF,
            ...(msg.type === "success"
              ? { background: "rgba(52,199,89,0.09)", color: "#1e7a3c", border: "1px solid rgba(52,199,89,0.18)" }
              : { background: "rgba(200,50,40,0.07)", color: "rgba(160,40,30,0.9)", border: "1px solid rgba(200,50,40,0.15)" }),
          }}>
            {msg.msg}
          </div>
        )}

        <button className="submit-btn" onClick={() => handleAssign(inv)} disabled={isSaving}
          style={{
            width: "100%", padding: "11px",
            background: "rgba(20,22,30,0.88)", border: "none", borderRadius: 11,
            color: "white", fontSize: 13, fontWeight: 600,
            cursor: isSaving ? "not-allowed" : "pointer",
            transition: "opacity 0.15s, transform 0.15s",
            opacity: isSaving ? 0.55 : 1, fontFamily: FF,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
            marginTop: f.billed_date ? 0 : 12,
          }}
        >
          {isSaving ? "Generating PDF…" : (
            <>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
              </svg>
              Assign & Generate PDF
            </>
          )}
        </button>
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ height: "calc(100vh - 24px)", display: "flex", flexDirection: "column", fontFamily: FF }}>
      <GlassCard style={{ maxWidth: "none", height: "100%", display: "flex", flexDirection: "column" }}>

        {/* ── Fixed header ─────────────────────────────────────────────── */}
        <div style={{ padding: "22px 28px 16px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "rgba(255,255,255,0.55)",
              border: "0.5px solid rgba(255,255,255,0.88)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                  stroke="rgba(50,60,90,0.6)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="14,2 14,8 20,8" stroke="rgba(50,60,90,0.6)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="16" y1="13" x2="8" y2="13" stroke="rgba(50,60,90,0.6)" strokeWidth="1.6" strokeLinecap="round"/>
                <line x1="16" y1="17" x2="8" y2="17" stroke="rgba(50,60,90,0.6)" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.4px" }}>Invoices</div>
              <div style={{ fontSize: 11, color: "rgba(60,60,67,0.38)", marginTop: 1 }}>9456-1438 Québec inc.</div>
            </div>
            <button onClick={fetchInvoices} disabled={loading}
              style={{
                marginLeft: "auto", display: "flex", alignItems: "center", gap: 6,
                padding: "7px 13px", borderRadius: 10,
                background: "rgba(255,255,255,0.52)", border: "0.5px solid rgba(255,255,255,0.82)",
                color: "rgba(60,60,67,0.6)", fontSize: 12, fontWeight: 500,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.5 : 1, fontFamily: FF,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: loading ? "spin 1s linear infinite" : "none" }}>
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
              </svg>
              Refresh
            </button>
          </div>

          <TabBar
            tabs={[
              { id: "assign", label: "Assign",      badge: draftInvoices.length },
              { id: "all",    label: "All Invoices", badge: 0 },
            ]}
            active={tab}
            onChange={setTab}
          />
        </div>

        <div style={{ height: "0.5px", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.65) 15%, rgba(255,255,255,0.65) 85%, transparent)", flexShrink: 0 }} />

        {/* ══ ASSIGN TAB ═══════════════════════════════════════════════ */}
        {tab === "assign" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>

            {/* Fixed stats */}
            <div style={{ padding: "18px 28px 14px", flexShrink: 0 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                <StatCard label="Draft" value={loading ? "—" : draftInvoices.length} sub="needs assigning" accent="rgba(255,249,235,0.6)" />
                <StatCard label="Unpaid" value={loading ? "—" : unpaidInvoices.length} sub="awaiting payment" accent="rgba(235,245,255,0.6)" />
                <StatCard label="Paid" value={loading ? "—" : paidInvoices.length} sub="collected" accent="rgba(240,252,244,0.65)" />
                <StatCard label="Pending Value" value={loading ? "—" : `$${fmtCAD(pendingValue)}`} sub="unpaid incl. HST" />
              </div>
            </div>

            {/* Scrollable draft list */}
            <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "0 28px" }}>

              {loading && <SkeletonRows count={6} height={58} />}

              {!loading && error && (
                <div style={{ padding: "32px 0", textAlign: "center" }}>
                  <div style={{ fontSize: 13, color: "rgba(160,40,30,0.7)", marginBottom: 12, fontFamily: FF }}>{error}</div>
                  <button onClick={fetchInvoices} style={{ padding: "7px 18px", background: "rgba(20,22,30,0.85)", border: "none", borderRadius: 9, color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FF }}>Retry</button>
                </div>
              )}

              {!loading && !error && draftInvoices.length === 0 && (
                <div style={{ padding: "50px 0", textAlign: "center" }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>✓</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#1c1c1e", marginBottom: 5, fontFamily: FF }}>All caught up</div>
                  <div style={{ fontSize: 13, color: "rgba(60,60,67,0.42)", fontFamily: FF }}>No draft invoices are waiting to be assigned.</div>
                </div>
              )}

              {!loading && !error && draftInvoices.length > 0 && (
                <div style={{
                  background: "rgba(255,255,255,0.42)",
                  border: "0.5px solid rgba(255,255,255,0.78)",
                  borderRadius: 16, overflow: "hidden", marginBottom: 20,
                }}>
                  {draftInvoices.map((inv, idx) => {
                    const id         = inv.row_number;
                    const isExpanded = expandedId === id;
                    const msg        = messages[id];
                    const total      = Number(inv.invoice_total) || 0;

                    return (
                      <div key={id} style={{ borderBottom: idx < draftInvoices.length - 1 ? "0.5px solid rgba(255,255,255,0.55)" : "none" }}>

                        {/* Row */}
                        <div style={{
                          display: "flex", alignItems: "center", gap: 14,
                          padding: "14px 20px",
                          background: isExpanded ? "rgba(255,255,255,0.22)" : "transparent",
                          transition: "background 0.12s",
                        }}>
                          {/* Status dot */}
                          <div style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: "rgba(255,149,0,0.65)" }} />

                          {/* Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "baseline", gap: 7, marginBottom: 3, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 14, fontWeight: 600, color: "#1c1c1e", letterSpacing: "-0.2px", fontFamily: FF }}>
                                {inv.campaign_name}
                              </span>
                              <span style={{ fontSize: 12, color: "rgba(60,60,67,0.45)", fontFamily: FF }}>{inv.brand}</span>
                            </div>
                            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                              <SPill status="draft" />
                              {inv.notes && (
                                <span style={{ fontSize: 11, color: "rgba(60,60,67,0.38)", fontFamily: FF }}>
                                  {inv.notes.length > 60 ? inv.notes.slice(0, 60) + "…" : inv.notes}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Amount */}
                          <div style={{ textAlign: "right", flexShrink: 0, minWidth: 72 }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.3px", fontFamily: FF }}>
                              ${fmt(total)}
                            </div>
                            <div style={{ fontSize: 10, color: "rgba(60,60,67,0.35)", fontFamily: FF }}>incl. HST</div>
                          </div>

                          {/* Assign button */}
                          <button className="assign-btn" onClick={() => toggleExpand(inv)}
                            style={{
                              padding: "7px 14px", borderRadius: 9,
                              border: "0.5px solid rgba(255,255,255,0.82)",
                              background: isExpanded ? "rgba(255,255,255,0.62)" : "rgba(255,255,255,0.48)",
                              color: isExpanded ? "#1c1c1e" : "rgba(50,60,90,0.7)",
                              fontSize: 12, fontWeight: 600, cursor: "pointer",
                              transition: "background 0.15s, border-color 0.15s",
                              fontFamily: FF, flexShrink: 0,
                              display: "flex", alignItems: "center", gap: 4,
                            }}
                          >
                            {isExpanded
                              ? <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="18,15 12,9 6,15"/></svg>Cancel</>
                              : <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20,6 9,17 4,12"/></svg>Assign</>}
                          </button>
                        </div>

                        {/* Success message (collapsed) */}
                        {msg && !isExpanded && (
                          <div style={{
                            margin: "0 20px 12px", padding: "8px 12px", borderRadius: 9,
                            fontSize: 12, fontWeight: 500, display: "flex", alignItems: "center", gap: 6, fontFamily: FF,
                            ...(msg.type === "success"
                              ? { background: "rgba(52,199,89,0.09)", color: "#1e7a3c", border: "1px solid rgba(52,199,89,0.18)" }
                              : { background: "rgba(200,50,40,0.07)", color: "rgba(160,40,30,0.9)", border: "1px solid rgba(200,50,40,0.15)" }),
                          }}>
                            {msg.type === "success"
                              ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20,6 9,17 4,12"/></svg>
                              : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/></svg>}
                            {msg.msg}
                          </div>
                        )}

                        {/* Expanded form */}
                        {isExpanded && (
                          <div style={{ padding: "0 20px" }}>
                            <AssignForm inv={inv} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ ALL INVOICES TAB ═════════════════════════════════════════ */}
        {tab === "all" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>

            {/* Fixed search + sort bar */}
            <div style={{ padding: "14px 28px 12px", flexShrink: 0 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                {/* Search */}
                <div style={{ position: "relative", flex: 1 }}>
                  <svg style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                    width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(60,60,67,0.35)" strokeWidth="2.2" strokeLinecap="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input className="g-input" style={{ ...INP, paddingLeft: 34 }}
                    placeholder="Search campaign or brand…"
                    value={allSearch}
                    onChange={(e) => { setAllSearch(e.target.value); setShowNoInvoices(false); }} />
                </div>

                {/* Sort buttons — hidden when "no invoices" toggle is on */}
                {!showNoInvoices && [
                  { id: "status",       label: "Status"     },
                  { id: "due_asc",      label: "Due ↑"      },
                  { id: "due_desc",     label: "Due ↓"      },
                  { id: "amount_desc",  label: "Amount ↓"   },
                  { id: "amount_asc",   label: "Amount ↑"   },
                ].map((s) => (
                  <button key={s.id} onClick={() => setAllSort(s.id)}
                    className="mode-btn"
                    style={{
                      padding: "9px 13px", borderRadius: 10, flexShrink: 0,
                      border: allSort === s.id ? "0.5px solid rgba(255,255,255,0.85)" : "0.5px solid rgba(255,255,255,0.7)",
                      background: allSort === s.id ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.42)",
                      color: allSort === s.id ? "#1c1c1e" : "rgba(60,60,67,0.55)",
                      fontSize: 12, fontWeight: allSort === s.id ? 600 : 400,
                      cursor: "pointer", fontFamily: FF, whiteSpace: "nowrap",
                      transition: "background 0.12s, border-color 0.12s",
                    }}
                  >
                    {s.label}
                  </button>
                ))}

                {/* No Invoices toggle */}
                <button
                  onClick={() => { setShowNoInvoices((v) => !v); setAllSearch(""); }}
                  className="mode-btn"
                  style={{
                    padding: "9px 13px", borderRadius: 10, flexShrink: 0,
                    border: showNoInvoices ? "0.5px solid rgba(255,149,0,0.45)" : "0.5px solid rgba(255,255,255,0.7)",
                    background: showNoInvoices ? "rgba(255,249,235,0.75)" : "rgba(255,255,255,0.42)",
                    color: showNoInvoices ? "rgba(140,80,0,0.9)" : "rgba(60,60,67,0.55)",
                    fontSize: 12, fontWeight: showNoInvoices ? 600 : 400,
                    cursor: "pointer", fontFamily: FF, whiteSpace: "nowrap",
                    display: "flex", alignItems: "center", gap: 5,
                    transition: "background 0.12s, border-color 0.12s",
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  No invoices
                  {campsFetched && campaignsWithNoInvoices.length > 0 && (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 20, background: "rgba(255,149,0,0.18)", color: "rgba(140,80,0,0.9)", border: "0.5px solid rgba(255,149,0,0.25)" }}>
                      {campaignsWithNoInvoices.length}
                    </span>
                  )}
                </button>
              </div>

              {allSearch && !showNoInvoices && (
                <div style={{ marginTop: 8, fontSize: 11, color: "rgba(60,60,67,0.38)", fontFamily: FF }}>
                  {filteredGroups.length} campaign{filteredGroups.length !== 1 ? "s" : ""} · filtered
                </div>
              )}
              {showNoInvoices && (
                <div style={{ marginTop: 8, fontSize: 11, color: "rgba(140,80,0,0.7)", fontFamily: FF }}>
                  {campsLoading ? "Loading campaigns…" : `${campaignsWithNoInvoices.length} campaign${campaignsWithNoInvoices.length !== 1 ? "s" : ""} with no invoices`}
                </div>
              )}
            </div>

            {/* Scrollable list */}
            <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "0 28px 28px" }}>

            {loading && (
              <div style={{ padding: "40px 0", textAlign: "center", color: "rgba(60,60,67,0.38)", fontSize: 14 }}>Loading invoices…</div>
            )}
            {!loading && error && (
              <div style={{ padding: "32px 0", textAlign: "center" }}>
                <div style={{ fontSize: 13, color: "rgba(160,40,30,0.7)", marginBottom: 12, fontFamily: FF }}>{error}</div>
                <button onClick={fetchInvoices} style={{ padding: "7px 18px", background: "rgba(20,22,30,0.85)", border: "none", borderRadius: 9, color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FF }}>Retry</button>
              </div>
            )}
            {!showNoInvoices && !loading && !error && invoices.length === 0 && (
              <div style={{ padding: "50px 0", textAlign: "center" }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#1c1c1e", marginBottom: 5, fontFamily: FF }}>No invoices yet</div>
                <div style={{ fontSize: 13, color: "rgba(60,60,67,0.42)", fontFamily: FF }}>Create a campaign with invoices to see them here.</div>
              </div>
            )}
            {!showNoInvoices && !loading && !error && invoices.length > 0 && filteredGroups.length === 0 && (
              <div style={{ padding: "40px 0", textAlign: "center", color: "rgba(60,60,67,0.38)", fontSize: 14 }}>
                No campaigns match your search.
              </div>
            )}

            {/* ── No Invoices view ─────────────────────────────────────────── */}
            {showNoInvoices && !campsLoading && campaignsWithNoInvoices.length === 0 && !loading && (
              <div style={{ padding: "50px 0", textAlign: "center" }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#1c1c1e", marginBottom: 5, fontFamily: FF }}>All campaigns have invoices</div>
                <div style={{ fontSize: 13, color: "rgba(60,60,67,0.42)", fontFamily: FF }}>Every active campaign has at least one invoice linked.</div>
              </div>
            )}

            {showNoInvoices && !campsLoading && campaignsWithNoInvoices.length > 0 && (
              <div style={{ background: "rgba(255,255,255,0.52)", backdropFilter: "blur(40px) saturate(180%)", WebkitBackdropFilter: "blur(40px) saturate(180%)", border: "0.5px solid rgba(255,149,0,0.22)", borderRadius: 16, overflow: "hidden", marginBottom: 20 }}>
                {campaignsWithNoInvoices.map((c, idx) => (
                  <div key={c.campaign_id} style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "14px 18px",
                    borderBottom: idx < campaignsWithNoInvoices.length - 1 ? "0.5px solid rgba(0,0,0,0.05)" : "none",
                  }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: "rgba(255,149,0,0.55)" }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "#1c1c1e", letterSpacing: "-0.2px", fontFamily: FF }}>{c.campaign_name}</span>
                        {c.brand && <span style={{ fontSize: 11, color: "rgba(60,60,67,0.45)", fontFamily: FF }}>{c.brand}</span>}
                        {c.status && <SPill status={c.status} />}
                      </div>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, color: "rgba(60,60,67,0.38)", fontFamily: FF }}>{c.campaign_id}</span>
                        {c.platform && <span style={{ fontSize: 11, color: "rgba(60,60,67,0.38)", fontFamily: FF }}>{c.platform}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      {c.deal_amount > 0 && (
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.3px", fontFamily: FF }}>
                          ${fmtCAD(c.deal_amount)}
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: "rgba(255,149,0,0.75)", fontWeight: 600, fontFamily: FF, marginTop: 2 }}>no invoices</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!showNoInvoices && !loading && !error && filteredGroups.length > 0 && filteredGroups.map((grp) => {
              const groupTotal = grp.invoices.reduce((s, i) => s + (Number(i.invoice_total) || 0), 0);

              return (
                <div key={grp.campaign_id || grp.campaign_name} style={{ marginBottom: 12 }}>
                  <div style={{
                    background: "rgba(255,255,255,0.52)",
                    backdropFilter: "blur(40px) saturate(180%)",
                    WebkitBackdropFilter: "blur(40px) saturate(180%)",
                    border: "0.5px solid rgba(255,255,255,0.85)",
                    borderRadius: 16, overflow: "hidden",
                  }}>
                    {/* Group header */}
                    <div style={{ padding: "13px 18px 11px", borderBottom: "0.5px solid rgba(0,0,0,0.05)", background: "rgba(255,255,255,0.25)" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.3px", fontFamily: FF }}>{grp.campaign_name}</span>
                          </div>
                          <span style={{ fontSize: 11, color: "rgba(60,60,67,0.48)", fontFamily: FF }}>{grp.brand}</span>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.4px", fontFamily: FF }}>
                            ${fmtCAD(groupTotal)}
                          </div>
                          <div style={{ fontSize: 10, color: "rgba(60,60,67,0.38)", fontFamily: FF }}>{grp.invoices.length} invoice{grp.invoices.length !== 1 ? "s" : ""}</div>
                        </div>
                      </div>
                    </div>

                    {/* Invoice rows */}
                    <div style={{ padding: "4px 0" }}>
                      {grp.invoices.map((inv, idx) => {
                        const isDraft = !inv.invoice_number;
                        const dueDate = fmtDate(inv.due_date);

                        return (
                          <div key={inv.row_number} style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "11px 18px",
                            borderBottom: idx < grp.invoices.length - 1 ? "0.5px solid rgba(0,0,0,0.04)" : "none",
                            flexWrap: "wrap",
                          }}>
                            <div style={{ width: 22, height: 22, borderRadius: 6, background: "rgba(255,255,255,0.55)", border: "0.5px solid rgba(255,255,255,0.85)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(60,60,67,0.48)", fontFamily: FF }}>{idx + 1}</span>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 2 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: isDraft ? "rgba(60,60,67,0.45)" : "#1c1c1e", fontFamily: FF }}>
                                  {inv.invoice_number ? `#${inv.invoice_number}` : "No number yet"}
                                </span>
                                <SPill status={inv.status || "draft"} />
                              </div>
                              {!isDraft && (
                                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                  {inv.billed_date && <span style={{ fontSize: 11, color: "rgba(60,60,67,0.42)", fontFamily: FF }}>{fmtDate(inv.billed_date)}</span>}
                                  {dueDate && <span style={{ fontSize: 11, color: "rgba(60,60,67,0.42)", fontFamily: FF }}>Due {dueDate}</span>}
                                  {inv.paid_date && <span style={{ fontSize: 11, color: "rgba(52,170,68,0.7)", fontFamily: FF }}>Paid {fmtDate(inv.paid_date)}</span>}
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: isDraft ? "rgba(60,60,67,0.45)" : "#1c1c1e", letterSpacing: "-0.2px", fontFamily: FF }}>
                                ${fmt(Number(inv.invoice_total) || 0)}
                              </div>
                              <div style={{ fontSize: 10, color: "rgba(60,60,67,0.35)", fontFamily: FF }}>incl. HST</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>{/* end scrollable list */}

          </div>
        )}

      </GlassCard>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
